// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {Box, Typography, Stack, TextField, MultiSelect, InputAdornment, IconButton, Skeleton} from '@deere/isg.component-library';
import {IconOrderable} from '@deere/icons';
import {CUSTOM_ROUTE, DEFAULT_TRAVEL_ROUTES} from 'Ui/constants/workboard-constants';
import ReadOnlyWrapperComponent from 'Ui/components/common/form/read-only/read-only-wrapper-component';
import HoleInput from 'OnLabor/workboard/common/hole-input';
import ValidationInput from 'Ui/components/common/form/validation-input';
import FormValidator from 'Ui/components/higher-order-components/form-validator';
import {capitalizeFirstLetter, replaceTranslationNames} from 'Utils/translation-utils';
import {formatNumber} from 'Utils/unit-conversion-utils';
import {isNullOrUndefined} from 'Common/utils/validation-utils';
import equipmentStatus from 'Common/constants/equipment-status';
import {formatEquipmentCutHeight, formatStatus} from 'OnEquip/equipment/utils/equipment-detail-formatters';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {JOB} from 'OnLabor/workboard/constants/workboard-creation-flows';
import {MINUTES_PER_HOUR} from 'Ui/constants/time-constants';
import {connect} from 'react-redux';
import {getEquipmentTypesModels} from './utils/assign-helpers';

const ICON_ORDERABLE_STYLE = {
    primary: '#C7C6C5',
    style: {
        height: '24px',
        width: '24px'
    }
};

const MAX_NOTE_LENGTH = 250;
const FIXED_LENGTH = 2;
const DISABLED_EQUIPMENT_STATUSES = [equipmentStatus.inShop, equipmentStatus.inUse];

function getFormattedEquipmentItem(equipmentWithStatus, formattedStatus, unitOfMeasure, translations) {
    const hoursPrefix = isNullOrUndefined(equipmentWithStatus.lastMeterHours) ?
        `--- ${translations.HOURS.toLowerCase()}` :
        `${formatNumber(equipmentWithStatus.lastMeterHours, {
            maximumFractionDigits: 0
        })} ${translations.HOURS.toLowerCase()}`;

    const cutHeight = !equipmentWithStatus.isMowingEquipment ? '' : ` | ${formatEquipmentCutHeight(equipmentWithStatus.cutHeight, unitOfMeasure, translations).formattedCutHeight}`;

    const title = formattedStatus === translations.AVAILABLE ? equipmentWithStatus.equipmentName : `${equipmentWithStatus.equipmentName} (${formattedStatus})`;

    return {
        disabled: DISABLED_EQUIPMENT_STATUSES.includes(equipmentWithStatus.status),
        id: equipmentWithStatus.equipmentId,
        subtitle: `${hoursPrefix}${cutHeight}`,
        title
    };
}

function getEquipmentItem(equipment, equipmentInUse, translations, unitOfMeasure, isFleetToEquipmentEnabled) {
    if (equipment.equipmentId === '') {
        return {
            id: equipment.equipmentId,
            title: equipment.equipmentName
        };
    }

    const equipmentWithStatus = {
        ...equipment,
        status: equipmentInUse.includes(equipment.equipmentId) ?
            equipmentStatus.inUse :
            equipment.status
    };

    const formattedStatus = formatStatus(equipmentWithStatus, translations);

    if (isFleetToEquipmentEnabled) {
        return getFormattedEquipmentItem(equipmentWithStatus, formattedStatus, unitOfMeasure, translations);
    }
    const hoursPrefix = isNullOrUndefined(equipmentWithStatus.lastMeterHours) ?
        `--- ${translations.HOURS.toLowerCase()} - ` :
        `${formatNumber(equipmentWithStatus.lastMeterHours, {
            maximumFractionDigits: 0
        })} ${translations.HOURS.toLowerCase()} - `;

    return {
        disabled: DISABLED_EQUIPMENT_STATUSES.includes(equipmentWithStatus.status),
        id: equipmentWithStatus.equipmentId,
        subtitle: `${hoursPrefix}${formattedStatus}`,
        title: equipmentWithStatus.equipmentName
    };
}

function groupSelectedEquipmentByType(workItem, isFleetToEquipmentEnabled = false) {
    if (!workItem) {
        return {};
    }
    if (isFleetToEquipmentEnabled) {
        return workItem.equipment.reduce((acc, eq) => {
            const {equipmentId} = eq;
            const equipmentType = eq.isgType?.name || eq.equipmentType;

            if (!acc[equipmentType]) {
                acc[equipmentType] = [];
            }
            acc[equipmentType].push(equipmentId);
            return acc;
        }, {});
    }
    const result = workItem.equipment.reduce((acc, eq) => {
        const {
            equipmentType, equipmentId
        } = eq;

        if (!acc[equipmentType]) {
            acc[equipmentType] = [];
        }
        acc[equipmentType].push(equipmentId);
        return acc;
    }, {});

    return result;
}

function filterSelectedEquipment(workItem, selectedIds, type, equipment, isFleetToEquipmentEnabled) {
    const prev = workItem.equipment.filter((eq) => {
        const eqType = isFleetToEquipmentEnabled ? eq.isgType?.name || eq.equipmentType : eq.equipmentType;

        return eqType !== type;
    });
    const next = selectedIds.includes('') ? [] : equipment.filter((eq) => selectedIds.includes(eq.equipmentId));

    return [...prev, ...next];
}

// Matches the MultiSelect built-in sort (numeric, case-insensitive) that disableSorting turns off.
const equipmentCollator = new Intl.Collator(undefined, {
    sensitivity: 'base',
    numeric: true
});

function sortItemsWithNonePinned(items) {
    const noneItems = items.filter((item) => item.id === '');
    const rest = items.filter((item) => item.id !== '')
        .sort((a, b) => equipmentCollator.compare(String(a.title), String(b.title)));

    return [...rest, ...noneItems];
}

function AssignDetail(props) {
    const {
        equipment = [],
        equipmentInUse = [],
        equipmentLoading = false,
        fleetId,
        isFleetToEquipmentEnabled,
        jobs = [],
        workItem,
        unitOfMeasure,
        updateWorkItem,
        setValid,
        translations,
        groupBy
    } = props;

    const operatorName = `${workItem?.teamMember?.firstName} ${workItem?.teamMember?.lastName}
        ${workItem?.teamMember?.onTimeOff ? ` - ${translations.NOT_AVAILABLE}` : ''}`;
    const job = jobs.find((job) => job.id === workItem?.jobId);
    const disabled = !workItem;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition
    } = useSortable({
        id: workItem?.workItemId
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    const values = {
        note: workItem?.note ?? '',
        actualDuration: workItem?.actualDuration ?? workItem?.actualHours ?? 0,
        equipment: groupSelectedEquipmentByType(workItem, isFleetToEquipmentEnabled)
    };

    const [route, setRoute] = React.useState(workItem?.route ?? {
        selectedRoute: '',
        routeCustomDefn: []
    });

    const [notesError, setNotesError] = React.useState(false);

    const charactersText = replaceTranslationNames(translations.ONLINK_SMS_CHAR_REMAINING, {
        '0': values.note.length > MAX_NOTE_LENGTH ? 0 : MAX_NOTE_LENGTH - values.note.length,
        '1': MAX_NOTE_LENGTH.toString()
    });

    const onChange = (event) => {
        const {
            name,
            value
        } = event.target;

        if (name === 'note') {
            setNotesError(false);
        }

        if (name === 'note' && value.length > MAX_NOTE_LENGTH) {
            setNotesError(true);
            return;
        }

        updateWorkItem({
            [name]: value
        });
    };

    const equipmentTypes = React.useMemo(() => {
        if (isFleetToEquipmentEnabled) {
            return getEquipmentTypesModels(job, equipment, capitalizeFirstLetter, translations);
        }

        const equipmentInJob = job?.equipmentTypes?.map((eq) => eq.name) || [];
        const result = equipment.reduce((acc, eq) => {
            const {
                equipmentType, equipmentArea
            } = eq;

            if (eq.status === equipmentStatus.archived) {
                return acc;
            }

            if (eq.fleetInUseId !== fleetId) {
                return acc;
            }

            if (!equipmentInJob?.includes(equipmentType)) {
                return acc;
            }

            if (equipmentArea && equipmentArea !== job?.equipmentArea) {
                return acc;
            }

            if (!acc[equipmentType]) {
                acc[equipmentType] = [{
                    equipmentId: '',
                    equipmentName: capitalizeFirstLetter(translations.NONE)
                }];
            }
            acc[equipmentType].push(eq);
            return acc;
        }, {});

        return result;
    }, [job, equipment, fleetId, isFleetToEquipmentEnabled, translations]);

    const travelRoutes = React.useMemo(() => {
        const routes = DEFAULT_TRAVEL_ROUTES.map((travelRoute) => ({
            id: travelRoute.id,
            title: translations[travelRoute.title]
        }));

        routes.push({
            id: '',
            title: capitalizeFirstLetter(translations.NONE)
        });
        return routes;
    }, [translations]);

    const handleRouteChange = React.useCallback(
        (name, value) => {
            let newRoute = {
                ...route,
                [name]: value
            };

            if (name === 'selectedRoute' && value !== CUSTOM_ROUTE) {
                newRoute = {
                    ...newRoute,
                    routeCustomDefn: []
                };
            }
            setRoute(newRoute);
            updateWorkItem({
                route: newRoute
            });
        },
        [route, updateWorkItem]
    );

    return (
        <div
            ref={setNodeRef}
            style={style}
        >
            <Box
                sx={{
                    m: '16px',
                    bgcolor: 'white',
                    p: '16px',
                    borderRadius: '4px'
                }}
            >
                {disabled ?
                    <Stack
                        sx={{
                            flexDirection: 'col',
                            alignItems: 'center'
                        }}
                    >
                        <Typography
                            gutterBottom
                            sx={{
                                textTransform: 'capitalize'
                            }}
                            variant='h3'
                        >
                            {groupBy === JOB ? translations.NO_OPERATOR_ASSIGNED_TITLE : translations.NO_JOB_ASSIGNED_TITLE}
                        </Typography>
                        <Typography variant='bodyMedium'>
                            {groupBy === JOB ? translations.NO_OPERATOR_ASSIGNED_BODY : translations.NO_JOB_ASSIGNED_BODY}
                        </Typography>
                    </Stack> :
                    <Stack
                        sx={{
                            justifyContent: 'space-between',
                            flexDirection: 'row',
                            alignItems: 'center'
                        }}
                    >
                        <Typography variant='h3'>
                            {groupBy === JOB ? operatorName : job?.title}
                        </Typography>
                        <IconButton
                            {...attributes}
                            {...listeners}
                            variant='borderless'
                        >
                            <IconOrderable {...ICON_ORDERABLE_STYLE}/>
                        </IconButton>
                    </Stack>}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        mt: '16px'
                    }}
                >
                    <ReadOnlyWrapperComponent
                        label={translations.NOTES}
                        value={values.note || ''}
                        wrappedComponent={TextField}
                        wrappedProps={{
                            disabled,
                            error: notesError,
                            fullWidth: true,
                            multiline: true,
                            className: 'notes-text-area',
                            name: 'note',
                            minRows: 5,
                            onChange,
                            placeholder: translations.CONSTRUCTION_ENTER_NOTES,
                            helperText: charactersText,
                            tabIndex: 0
                        }}
                    />
                    {!disabled && equipmentLoading ? (
                        <Stack
                            data-testid='assign-detail-equipment-loading'
                            spacing='16px'
                        >
                            {[0, 1].map((i) => (
                                <Skeleton
                                    animation='pulse'
                                    height={28}
                                    key={i}
                                    variant='rounded'
                                    width='100%'
                                />
                            ))}
                        </Stack>
                    ) : Object.keys(equipmentTypes).length > 0 && Object.entries(equipmentTypes).map(([type, equipment]) => (
                        <ReadOnlyWrapperComponent
                            key={type}
                            wrappedComponent={MultiSelect}
                            wrappedProps={{
                                disabled,
                                disableSorting: true,
                                items: sortItemsWithNonePinned(equipment.map((eq) => getEquipmentItem(eq, equipmentInUse, translations, unitOfMeasure, isFleetToEquipmentEnabled))),
                                label: type,
                                labels: {
                                    multiSelect: translations.EQUIPMENT,
                                    placeholder: translations.FILES_SELECT_EQUIPMENT,
                                    emptyList: translations.NO_ITEMS,
                                    noItemMatch: translations.NO_RESULTS_FOUNDS
                                },
                                onChange: (selectedIds) => {
                                    const selectedEquipment = filterSelectedEquipment(workItem, selectedIds, type, equipment, isFleetToEquipmentEnabled);

                                    updateWorkItem({
                                        equipment: selectedEquipment
                                    });
                                },
                                selectedIds: values.equipment[type],
                                single: true
                            }}
                        />
                    ))}
                    <ReadOnlyWrapperComponent
                        label={translations.ONLINK_ROUTE}
                        wrappedComponent={MultiSelect}
                        wrappedProps={{
                            disabled,
                            autoFocus: true,
                            disableSorting: true,
                            items: travelRoutes,
                            labels: {
                                placeholder: translations.ONLINK_SELECT_ROUTE
                            },
                            onChange: (ids) => handleRouteChange('selectedRoute', ids[0]),
                            selectedIds: [route.selectedRoute].filter(Boolean),
                            single: true
                        }}
                    />
                    {
                        route.selectedRoute === CUSTOM_ROUTE &&
                        <ReadOnlyWrapperComponent
                            label={translations.ONLINK_HOLES}
                            wrappedComponent={HoleInput}
                            wrappedProps={{
                                disabled,
                                onChange: (ids) => handleRouteChange('routeCustomDefn', ids),
                                routeCustomDefn: route.routeCustomDefn,
                                translations,
                                width: '100%'
                            }}
                        />
                    }
                    <ReadOnlyWrapperComponent
                        label={translations.ONLINK_ACTUAL_HOURS}
                        value={Number((values.actualDuration / MINUTES_PER_HOUR).toFixed(FIXED_LENGTH)) || ''}
                        wrappedComponent={ValidationInput}
                        wrappedProps={{
                            disabled,
                            fullWidth: true,
                            name: 'actualDuration',
                            onChange,
                            placeholder: translations.ONLINK_ACTUAL_HOURS_PLACEHOLDER,
                            tabIndex: 0,
                            setValid,
                            input: {
                                endAdornment: <InputAdornment position='end'>{translations.HOUR_UOM}</InputAdornment>
                            },
                            max: 24,
                            min: 0,
                            step: 'any',
                            type: 'number',
                            errors: {
                                rangeOverflow: `${translations.ONLINK_VALUE_LESS_THAN_OR_EQUAL} 24.`,
                                rangeUnderflow: `${translations.ONLINK_VALUE_GREATER_THAN_OR_EQUAL} 0.`
                            }
                        }}
                    />
                </Box>
            </Box>
        </div>
    );
}

AssignDetail.propTypes = {
    equipment: PropTypes.arrayOf(PropTypes.object),
    equipmentInUse: PropTypes.array,
    equipmentLoading: PropTypes.bool,
    fleetId: PropTypes.string,
    groupBy: PropTypes.string,
    isFleetToEquipmentEnabled: PropTypes.bool,
    jobs: PropTypes.arrayOf(PropTypes.object),
    setValid: PropTypes.func,
    translations: PropTypes.translations,
    unitOfMeasure: PropTypes.string,
    updateWorkItem: PropTypes.func,
    workItem: PropTypes.object
};

export function mapStateToProps(state) {
    return {
        unitOfMeasure: state.membership.unitOfMeasure
    };
}

export default connect(mapStateToProps)(FormValidator(AssignDetail));
